import { ChangeDetectionStrategy, Component, Injector, Input, ViewEncapsulation } from '@angular/core';
import { COMMA } from '@angular/cdk/keycodes';
import { AppTextService } from '@fi-shared/app-text';
import { MatChipGrid, MatChipInputEvent } from '@angular/material/chips';
import { FiCustomValidatorPatterns } from '@fi-shared/fi-custom-validator-patterns/fi-custom-validator-patterns';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { FiUserContactTypeEnum } from '@fi-shared/fi-user-contact-chip-grid-input/fi-user-contact-type.enum';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';

@Component({
    selector: 'fi-user-contact-chip-grid-input',
    template: `
        <mat-form-field appearance="outline" class="full-width">
            <mat-label>{{ getUserContactInputLabel() }}</mat-label>
            <mat-chip-grid #chipList [required]="required">
                @for (userContact of userContacts; track userContact) {
                    <mat-chip-row class="fi-standard-chip-row" (removed)="removeUserContact(userContact)">
                        <span class="chip-value">{{ userContact }}</span>
                        <button mat-icon-button matChipRemove>
                            <mat-icon class="far fa-circle-xmark" />
                        </button>
                    </mat-chip-row>
                }
                <input
                    [matChipInputFor]="chipList"
                    [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                    [matChipInputAddOnBlur]="true"
                    (matChipInputTokenEnd)="addUserContact($event, chipList)"
                    [placeholder]="getUserContactInputPlaceholder()"
                />
            </mat-chip-grid>
            <mat-hint>{{ getUserContactInputHint() }}</mat-hint>

            <mat-error>
                @if (chipList.errorState) {
                    {{ getUserContactErrorMessage() }}
                }
            </mat-error>
        </mat-form-field>
    `,
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-contact-chip-grid-input' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiUserContactChipGridInputComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiUserContactChipGridInputComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class FiUserContactChipGridInputComponent extends ControlValueAccessorValidatorBase<string[]> {
    @Input() required: boolean;
    @Input() userContactType: FiUserContactTypeEnum;

    get userContacts(): string[] {
        return this.ngModel;
    }

    readonly separatorKeysCodes: number[] = [COMMA];
    readonly phoneNumberPlaceholder: string = '+46 70 00 00 00';
    readonly emailPlaceholder: string = 'john.doe@example.com';

    constructor(
        private injector: Injector,
        private appTextService: AppTextService,
    ) {
        super(injector);
    }

    getUserContactInputLabel(): string {
        return this.appTextService.get(
            this.isPhoneNumberContactType() ? 'common.phone_number' : 'common.email_address',
        );
    }

    getUserContactInputHint(): string {
        return this.appTextService.get(
            this.isPhoneNumberContactType()
                ? 'checklist.checkpoint_action_phone_number_hint'
                : 'checklist.checkpoint_action_email_hint',
        );
    }

    getUserContactInputPlaceholder(): string {
        return this.isPhoneNumberContactType() ? this.phoneNumberPlaceholder : this.emailPlaceholder;
    }

    getUserContactErrorMessage(): string {
        return this.appTextService.get(this.isPhoneNumberContactType() ? 'phone_number.invalid' : 'email.invalid');
    }

    addUserContact(event: MatChipInputEvent, chipList: MatChipGrid): void {
        const value: string = (event.value || '').trim();
        if (value) {
            chipList.errorState = !this.isUserContactValid(value);
            if (chipList.errorState) {
                return;
            }
            this.userContacts.push(value);
            this.doPropagateChanges();
        }
        event.chipInput!.clear();
    }

    isUserContactValid(value: string): boolean {
        const regex = new RegExp(
            this.isPhoneNumberContactType() ? FiCustomValidatorPatterns.PhoneNumber : FiCustomValidatorPatterns.Email,
        );
        return regex.test(value);
    }

    removeUserContact(userContact: string) {
        const index: number = this.userContacts.indexOf(userContact);

        if (index >= 0) {
            this.userContacts.splice(index, 1);
            this.doPropagateChanges();
        }
    }

    private isPhoneNumberContactType(): boolean {
        return this.userContactType === FiUserContactTypeEnum.PhoneNumber;
    }

    protected readonly FiProfileSizes = FiProfileSizes;
}
